import { useEventListener } from '@passkeys/hooks';
import type { ReactNode } from 'react';
import { createContext, useCallback, useEffect, useState } from 'react';

import {
  setDocumentDataAttribute,
  setDocumentProperty,
} from '@/ui/utils/document';
import { WALLET_WIDGET_CONFIG_MESSAGE_TYPE } from '@/ui/utils/messages';
import { calculateColors, type Theme } from '@/ui/utils/theme';

// TODO: Dedupe.
export interface WidgetConfig {
  theme: Theme;
  size: 'small' | 'medium' | 'large' | 'huge';
  shape: 'rounded' | 'squared';
  compact: boolean;
  noAutoCompact: boolean;
  smallScreen: boolean;
}

export type WidgetConfigContextValue = WidgetConfig;

// TODO: Dedupe.
const defaultValue: WidgetConfigContextValue = {
  theme: {
    variant: 'glow',
    colors: {
      collapsed: {},
    },
  },
  size: 'medium',
  shape: 'rounded',
  compact: false,
  noAutoCompact: false,
  smallScreen: false,
};

const WidgetConfigContext =
  createContext<WidgetConfigContextValue>(defaultValue);

export interface WidgetConfigProviderProps {
  children: ReactNode;
}

export function WidgetConfigProvider({ children }: WidgetConfigProviderProps) {
  if (__STANDALONE_MODE__) {
    throw new Error(
      'WidgetConfigProvider should not be used in standalone mode',
    );
  }

  const [config, setConfig] = useState<WidgetConfig | null>(null);

  // Listen for config changes.
  const handleMessage = useCallback((event: MessageEvent) => {
    if (event.data?.type === WALLET_WIDGET_CONFIG_MESSAGE_TYPE) {
      setConfig(event.data.config);
    }
  }, []);
  useEventListener('message', handleMessage);

  // Set theme for TailwindCSS.
  useEffect(() => {
    const { theme } = config ?? {};
    if (theme) {
      const {
        accentColor,
        collapsedAccentColor,
        collapsedAvatarForegroundColor,
        collapsedBackgroundColor,
        collapsedForegroundColor,
        collapsedBorderColor,
        collapsedSkeletonColor,
        collapsedTextOpacity,
        collapsedIconOpacity,
      } = calculateColors(theme);

      setDocumentDataAttribute('theme', theme.variant);

      // When the accent color is set, the widget is set to mono mode.
      const isMonoMode = accentColor !== undefined;
      setDocumentDataAttribute('mode', isMonoMode ? 'mono' : null);

      setDocumentProperty('--accent-color', accentColor);
      setDocumentProperty('--collapsed-avatar-bg-color', collapsedAccentColor);
      setDocumentProperty(
        '--collapsed-avatar-logo-color',
        collapsedAvatarForegroundColor,
      );
      setDocumentProperty(
        '--collapsed-avatar-border-color',
        collapsedAvatarForegroundColor,
      );
      setDocumentProperty('--collapsed-bg-color', collapsedBackgroundColor);
      setDocumentProperty('--collapsed-border-color', collapsedBorderColor);
      setDocumentProperty('--collapsed-text-color', collapsedForegroundColor);
      setDocumentProperty('--collapsed-text-opacity', collapsedTextOpacity);
      setDocumentProperty('--collapsed-icon-opacity', collapsedIconOpacity);
      setDocumentProperty('--collapsed-skeleton-color', collapsedSkeletonColor);
    }
  }, [config]);

  if (config === null) {
    return null;
  }

  return (
    <WidgetConfigContext.Provider value={config}>
      {children}
    </WidgetConfigContext.Provider>
  );
}

export default WidgetConfigContext;
